import { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Tooltip from "@mui/material/Tooltip";
import SearchIcon from "@mui/icons-material/Search";
import { colorPalette } from "../styles/theme";
import { RISK_STATUS, RISK_FILTER_OPTIONS, DISTRESS_TO_RISK_FILTER } from "./participantStatus";
import {
  useWsDataset,
  getScopeParticipants,
  buildDailyScores,
  buildWeekSummary,
  getQuickRange,
} from "./wsData";
import { PHQ_BANDS, GAD_BANDS, getBandLabel, getScoreColor } from "./screeningBands";
import DailyDualLineChart, {
  DISTRESS_LEVELS,
  SIGNAL_LEVELS as SIGNAL_CHART_LEVELS,
  REGION_AVERAGE,
  DistressLevelLegend,
  buildDailyDualSeries,
} from "./DailyDualLineChart";
import SopGuideDrawer from "./SopGuideDrawer";

/**
 * 참가자 관리 (개인 상세) — WS 샘플 (실데이터 CSV 기반)
 * - 기본 조회 기간은 참가자의 마지막 활동일 기준 최근 7일입니다. (실데이터는 참가자마다 활동 종료 시점이 다름)
 * - 기존 개인 상세(UserDetailView 참가자 목록 탭)의 "좌측 목록 + 우측 상세" 레이아웃을 그대로 유지하고 내부 구성만 개선
 *   1) 좌측: 필터 2×2(위험도·활동상태·성별·검사이력) + 목록 우측 끝 상태 신호등/에피소드 배지
 *   2) 우측 상단: 현재 상태 태그 + 지표 해석 가이드(SOP) 버튼 (검사 내역 UI는 현행 유지)
 *   3) 우측 중단: 주간 요약을 영문 변수명 카드 대신 4개의 한글 비교형 카드로 개편 (소수점 원점수 비노출)
 */

const PAGE_SIZE = 10;
const DISPLAY_PAGE_COUNT = 5; // 페이지네이션 번호 그룹당 표시 개수
const DEFAULT_RANGE_DAYS = 7;
// 기관 평균과의 차이가 이 값 미만이면 "비슷"으로 판단 (0~1 스케일 점수 기준)
const SIMILAR_THRESHOLD = 0.05;
// 주간 요약 카드 4열 배치 최소 폭 — 이보다 좁으면 2열
const SUMMARY_FOUR_COLUMN_MIN_WIDTH = 760;

const genderLabel = (gender) => (gender === "M" ? "남" : gender === "F" ? "여" : "-");

// 활동 상태 배지 — 기존 app_usage 배지와 같은 스타일(solid Chip)
const ACTIVITY_LABEL = { active: "활발", inactive: "잠잠" };
const ACTIVITY_STYLE = {
  active: { backgroundColor: "#22c55e", color: "#fff" },
  inactive: { backgroundColor: "#9ca3af", color: "#fff" },
};

/** 참가자의 마지막 활동일(없으면 데이터 마지막 날짜) 기준 최근 7일 */
const getRecentRange = (participant) =>
  participant.lastActiveDate ? getQuickRange(DEFAULT_RANGE_DAYS, participant.lastActiveDate) : getQuickRange(DEFAULT_RANGE_DAYS);

// 2×2 배치에서 "활동상태 전체" 같은 기본 문구가 잘리지 않도록 글자·좌우 여백을 줄임
const filterSelectSx = {
  fontSize: 12,
  "& .MuiSelect-select": { py: 0.9, pl: 1.25, pr: "24px !important" },
  "& .MuiSelect-icon": { right: 2 },
};

// ─── 좌측 목록 ─────────────────────────────────────────────────────
/** 목록 우측 끝 상태 표시 — 위기/에피소드는 배지, 그 외는 신호등 점 + 단계명 */
const StatusIndicator = ({ status }) => {
  const config = RISK_STATUS[status];
  if (config.badge) {
    return (
      <Box
        component="span"
        sx={{
          flexShrink: 0,
          px: 0.75,
          py: 0.25,
          borderRadius: 1,
          fontSize: 11,
          fontWeight: 700,
          whiteSpace: "nowrap",
          ...config.badgeSx,
        }}
      >
        {config.badge}
      </Box>
    );
  }
  return (
    <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
      <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: config.dot }} />
      <Typography sx={{ fontSize: 11, fontWeight: 600, color: colorPalette.textSecondary, whiteSpace: "nowrap" }}>
        {config.label}
      </Typography>
    </Box>
  );
};

const ParticipantListItem = ({ participant, selected, onClick }) => (
  <ListItemButton
    selected={selected}
    onClick={onClick}
    sx={{
      display: "block",
      borderBottom: `1px solid ${colorPalette.border}`,
      backgroundColor: selected ? colorPalette.activeBg : "transparent",
      "&.Mui-selected": { backgroundColor: colorPalette.activeBg },
      "&:hover": { backgroundColor: colorPalette.activeBg },
    }}
  >
    {/* 1줄: 이름 + 활동 배지 … 우측 끝 상태 표시 (2줄 정보가 배지에 밀려 잘리지 않도록 1줄에 배치) */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Typography
        component="span"
        noWrap
        sx={{
          fontSize: 14,
          fontWeight: selected ? 700 : 500,
          color: selected ? colorPalette.primary : colorPalette.textPrimary,
        }}
      >
        {participant.name}
      </Typography>
      <Chip
        label={ACTIVITY_LABEL[participant.activity]}
        size="small"
        sx={{ height: 18, fontSize: 10, fontWeight: 700, ...ACTIVITY_STYLE[participant.activity] }}
      />
      <Box sx={{ ml: "auto", display: "flex" }}>
        <StatusIndicator status={participant.status} />
      </Box>
    </Box>
    <Typography noWrap sx={{ mt: 0.25, fontSize: 12, color: colorPalette.textSecondary }}>
      {participant.age ?? "-"}세 · {genderLabel(participant.gender)} · PHQ {participant.phq_history.length}회 · GAD{" "}
      {participant.gad_history.length}회
    </Typography>
  </ListItemButton>
);

const Pagination = ({ currentPage, pageCnt, onChange }) => {
  const pageGroup = Math.ceil(currentPage / DISPLAY_PAGE_COUNT);
  const startPage = (pageGroup - 1) * DISPLAY_PAGE_COUNT + 1;
  const endPage = Math.min(pageGroup * DISPLAY_PAGE_COUNT, pageCnt);
  const navSx = { minWidth: 26, px: 0.5, fontSize: 12 };

  // 버튼을 숨기지 않고 disabled 처리해 번호 위치가 고정되도록 함 (기존 목록과 동일)
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 0.3,
        justifyContent: "center",
        alignItems: "center",
        py: 1.2,
        px: 1,
        borderTop: `1px solid ${colorPalette.border}`,
        flexShrink: 0,
      }}
    >
      <Button size="small" disabled={currentPage === 1} onClick={() => onChange(1)} sx={navSx}>
        &lt;&lt;
      </Button>
      <Button size="small" disabled={currentPage <= 1} onClick={() => onChange(currentPage - 1)} sx={navSx}>
        &lt;
      </Button>
      {Array.from({ length: endPage - startPage + 1 }, (_, index) => {
        const pageNumber = startPage + index;
        const isActive = currentPage === pageNumber;
        return (
          <Button
            key={pageNumber}
            size="small"
            variant={isActive ? "contained" : "text"}
            onClick={() => onChange(pageNumber)}
            sx={{ ...navSx, color: isActive ? colorPalette.white : colorPalette.textSecondary }}
          >
            {pageNumber}
          </Button>
        );
      })}
      <Button size="small" disabled={currentPage >= pageCnt} onClick={() => onChange(currentPage + 1)} sx={navSx}>
        &gt;
      </Button>
      <Button size="small" disabled={currentPage === pageCnt} onClick={() => onChange(pageCnt)} sx={navSx}>
        &gt;&gt;
      </Button>
    </Box>
  );
};

// ─── 우측 상단: 검사 내역 (현행 유지) ─────────────────────────────────
/** 검사 요청 버튼 — 기존과 같은 확인창 → 결과 알림 흐름 (샘플은 실제 발송 없음) */
const SurveyRequestButton = ({ label }) => {
  const [requested, setRequested] = useState(false);

  const handleRequest = () => {
    if (!window.confirm(`${label} 요청을 발송하시겠습니까?`)) return;
    setRequested(true);
    alert(`(샘플) ${label} 요청이 정상적으로 발송되었습니다.`);
  };

  return (
    <Button
      size="small"
      variant="outlined"
      disabled={requested}
      onClick={handleRequest}
      sx={{ minWidth: 64, height: 24, px: 1, fontSize: 11, fontWeight: 700 }}
    >
      {requested ? "요청됨" : "검사 요청"}
    </Button>
  );
};

/** PHQ-9 / GAD-7 회차별 이력 — 기존 HistoryList와 같은 모양 (1차 · 날짜 · 해석 구간 · 점수) */
const HistoryList = ({ label, type, history }) => {
  const bands = type === "phq" ? PHQ_BANDS : GAD_BANDS;
  return (
    <Box sx={{ flex: 1, minWidth: 260 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: colorPalette.textSecondary }}>
          {label} ({history.length}회)
        </Typography>
        <SurveyRequestButton label={label} />
      </Box>
      {history.length === 0 ? (
        <Box
          sx={{
            py: 3,
            textAlign: "center",
            border: `1px dashed ${colorPalette.border}`,
            borderRadius: 1,
            color: colorPalette.textSecondary,
          }}
        >
          <Typography variant="caption">검사 이력이 없습니다.</Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {history.map((item) => (
            <Box
              key={item.attempt}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: 1,
                border: `1px solid ${colorPalette.border}`,
                backgroundColor: colorPalette.white,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: colorPalette.primary }}>
                  {item.attempt}차
                </Typography>
                <Typography variant="caption" sx={{ color: colorPalette.textSecondary }}>
                  {item.completed_at}
                </Typography>
                <Chip
                  label={getBandLabel(bands, item.total_score)}
                  size="small"
                  sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                />
              </Box>
              <Chip
                label={`${item.total_score}점`}
                size="small"
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 700,
                  backgroundColor: getScoreColor(type, item.total_score),
                  color: "#111827",
                }}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─── 우측 중단: 주간 요약 4개 한글 비교 카드 ─────────────────────────────
// 종합 디스트레스 4단계 (개인 상세 DISTRESS_LEVELS와 같은 경계) — 괄호는 정성적 수준
const DISTRESS_STAGES = [
  { max: 0.16, emoji: "🟢", label: "안정", level: "낮음", color: "#22c55e", text: "#15803d", bg: "#dcfce7" },
  { max: 0.41, emoji: "🟡", label: "주의", level: "보통", color: "#facc15", text: "#a16207", bg: "#fef9c3" },
  { max: 0.6, emoji: "🟠", label: "경고", level: "높음", color: "#f97316", text: "#c2410c", bg: "#ffedd5" },
  { max: 1, emoji: "🔴", label: "위험", level: "매우 높음", color: "#ef4444", text: "#b91c1c", bg: "#fee2e2" },
];
const getDistressStage = (value) => DISTRESS_STAGES.find((stage) => value <= stage.max) ?? DISTRESS_STAGES[3];

// 증상 부담·부정 정서 정성적 단계 (0~1 점수를 4구간으로 환산, 값이 없으면 "기록 없음")
const SIGNAL_LEVELS = [
  { max: 0.25, label: "낮음", text: "#15803d", bg: "#dcfce7" },
  { max: 0.5, label: "보통", text: "#a16207", bg: "#fef9c3" },
  { max: 0.75, label: "높음", text: "#c2410c", bg: "#ffedd5" },
  { max: 1, label: "매우높음", text: "#b91c1c", bg: "#fee2e2" },
];
const NO_RECORD_LEVEL = { label: "기록 없음", text: "#6b7280", bg: colorPalette.secondary };
const getSignalLevel = (value) =>
  typeof value === "number" ? SIGNAL_LEVELS.find((level) => value <= level.max) ?? SIGNAL_LEVELS[3] : NO_RECORD_LEVEL;

const formatDotDate = (dateStr) => dateStr.replaceAll("-", ".");

/** 개인 값과 기관 평균 비교 — 차이가 SIMILAR_THRESHOLD 미만이면 "유사" */
const compareToOrg = (userValue, orgValue) => {
  if (typeof userValue !== "number" || typeof orgValue !== "number") return null;
  const diff = userValue - orgValue;
  if (Math.abs(diff) < SIMILAR_THRESHOLD) return "similar";
  return diff > 0 ? "higher" : "lower";
};

const SummaryCard = ({ title, highlight, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      display: "flex",
      flexDirection: "column",
      gap: 1,
      minWidth: 0,
      // 강조 카드(에피소드 발생 등)는 연한 붉은 배경
      backgroundColor: highlight ? "#fef2f2" : colorPalette.white,
      borderColor: highlight ? "#fecaca" : colorPalette.border,
    }}
  >
    <Typography sx={{ fontSize: 12, fontWeight: 700, color: colorPalette.textSecondary }}>{title}</Typography>
    {children}
  </Paper>
);

const SubText = ({ children }) => (
  <Typography component="div" sx={{ fontSize: 12, color: colorPalette.textSecondary, lineHeight: 1.6 }}>
    {children}
  </Typography>
);

/** 1. 조회 기간 및 참여 — period_start/end, org_respondents/org_total */
const PeriodCard = ({ week }) => (
  <SummaryCard title="조회 기간 및 참여">
    <Typography sx={{ fontSize: 16, fontWeight: 800, color: colorPalette.textPrimary, lineHeight: 1.3 }}>
      {formatDotDate(week.period_start)} ~ {formatDotDate(week.period_end)}
    </Typography>
    <SubText>
      기간 내 기록일: <b>{week.record_days}일</b> | 기관 전체 활동: <b>{week.org_respondents}</b> / {week.org_total}명
    </SubText>
  </SummaryCard>
);

/** 2. 종합 디스트레스 수준 — distress_avg vs org_distress_avg를 4단계 배지 + 수평 게이지로 표시 (숫자 비노출) */
const DistressCard = ({ week }) => {
  const hasValue = typeof week.distress_avg === "number";
  const userStage = hasValue ? getDistressStage(week.distress_avg) : null;
  const orgStage = getDistressStage(week.org_distress_avg);
  const compare = compareToOrg(week.distress_avg, week.org_distress_avg);
  const orgLabel = `기관 평균 수준(${orgStage.emoji} ${orgStage.label})`;
  const compareText = {
    similar: `${orgLabel}과 유사함`,
    higher: `${orgLabel}보다 높음`,
    lower: `${orgLabel}보다 낮음`,
  }[compare];

  return (
    <SummaryCard title="종합 디스트레스 수준">
      {hasValue ? (
        <Box
          component="span"
          sx={{
            alignSelf: "flex-start",
            px: 1,
            py: 0.4,
            borderRadius: 1,
            fontSize: 14,
            fontWeight: 800,
            color: userStage.text,
            backgroundColor: userStage.bg,
          }}
        >
          {userStage.emoji} {userStage.label} ({userStage.level})
        </Box>
      ) : (
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: NO_RECORD_LEVEL.text }}>기록 없음</Typography>
      )}

      {/* 수평 게이지 — 4단계 구간 위에 개인 위치(채움)와 기관 평균(세로 눈금) 표시 */}
      <Box sx={{ position: "relative", mt: 0.5, mb: 0.25 }}>
        <Box sx={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", gap: "2px" }}>
          {DISTRESS_STAGES.map((stage, i) => {
            const min = i === 0 ? 0 : DISTRESS_STAGES[i - 1].max;
            const active = hasValue && week.distress_avg > min;
            return (
              <Box
                key={stage.label}
                sx={{
                  flex: stage.max - min,
                  backgroundColor: active ? stage.color : colorPalette.secondary,
                  opacity: active && stage !== userStage ? 0.45 : 1,
                }}
              />
            );
          })}
        </Box>
        <Tooltip title={`기관 평균: ${orgStage.label}`} arrow placement="top">
          <Box
            sx={{
              position: "absolute",
              top: -3,
              left: `calc(${Math.min(Math.max(week.org_distress_avg, 0), 1) * 100}% - 1px)`,
              width: 2,
              height: 14,
              borderRadius: 1,
              backgroundColor: colorPalette.textPrimary,
            }}
          />
        </Tooltip>
      </Box>
      <SubText>{hasValue ? compareText : orgLabel}</SubText>
    </SummaryCard>
  );
};

/** 3. 주요 신호 요약 — 증상 부담(symptom)·부정 정서(emo)를 정성적 단계로 표시 (숫자 비노출) */
const SignalRow = ({ label, userValue, orgValue }) => {
  const user = getSignalLevel(userValue);
  const org = getSignalLevel(orgValue);
  return (
    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 }}>
      <Typography sx={{ width: 64, flexShrink: 0, fontSize: 12, fontWeight: 700, color: colorPalette.textPrimary }}>
        {label}
      </Typography>
      <Box
        component="span"
        sx={{ px: 0.75, py: 0.2, borderRadius: 1, fontSize: 12, fontWeight: 700, color: user.text, backgroundColor: user.bg }}
      >
        {user.label}
      </Box>
      <Typography sx={{ fontSize: 11, color: colorPalette.textSecondary, whiteSpace: "nowrap" }}>
        (기관 평균: {org.label})
      </Typography>
    </Box>
  );
};

const SignalCard = ({ week }) => (
  <SummaryCard title="주요 신호 요약 (증상·감정)">
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 0.25 }}>
      <SignalRow label="증상 부담" userValue={week.symptom_avg} orgValue={week.org_symptom_avg} />
      <SignalRow label="부정 정서" userValue={week.emo_avg} orgValue={week.org_emo_avg} />
    </Box>
  </SummaryCard>
);

/** 4. 급성 악화 (에피소드) — 1회 이상이면 카드 배경 연한 붉은색 */
const EpisodeCard = ({ week }) => {
  const count = week.episode_count;
  // 현재 상태: 발생 없음 → 정상 / 조회 종료일 기준 최근 2일 내 발생 → 진행 중 / 그 외 → 발생 이력 있음
  const state = count === 0 ? "정상" : week.recent_episode ? "진행 중" : "발생 이력 있음";
  return (
    <SummaryCard title="급성 악화 (에피소드)" highlight={count > 0}>
      <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.3, color: count > 0 ? "#dc2626" : colorPalette.textPrimary }}>
        기간 내 {count}회 발생
      </Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: count > 0 ? "#dc2626" : "#15803d" }}>
        (현재 상태: {state})
      </Typography>
      <SubText>기관 1인당 평균 발생: {week.org_episode_avg}회</SubText>
    </SummaryCard>
  );
};

/** 주간 요약 — 기존 영문 변수명 카드 11개를 4개 한글 비교 카드로 통합 (소수점 원점수 비노출) */
const WeeklySummaryCards = ({ week }) => (
  <Box sx={{ containerType: "inline-size" }}>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
        gap: 1.5,
        [`@container (max-width: ${SUMMARY_FOUR_COLUMN_MIN_WIDTH - 1}px)`]: {
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        },
      }}
    >
      <PeriodCard week={week} />
      <DistressCard week={week} />
      <SignalCard week={week} />
      <EpisodeCard week={week} />
    </Box>
  </Box>
);

// ─── 우측 상세 ─────────────────────────────────────────────────────
const ParticipantDetail = ({ participant, scopeKey }) => {
  const dataset = useWsDataset();
  const [sopOpen, setSopOpen] = useState(false);
  const [appliedRange, setAppliedRange] = useState(() => getRecentRange(participant));
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");

  const daily = useMemo(
    () => buildDailyScores(dataset, participant, scopeKey, appliedRange.start, appliedRange.end),
    [dataset, participant, scopeKey, appliedRange],
  );
  const week = useMemo(
    () => buildWeekSummary(dataset, daily, scopeKey, appliedRange.start, appliedRange.end),
    [dataset, daily, scopeKey, appliedRange],
  );
  const status = RISK_STATUS[participant.status];

  // 기존과 동일: 기간을 비워두고 조회하면 최근 7일 기준으로 다시 표시
  const handleRangeSearch = () => {
    if (rangeStart && rangeEnd && rangeStart <= rangeEnd) setAppliedRange({ start: rangeStart, end: rangeEnd });
    else if (!rangeStart && !rangeEnd) setAppliedRange(getRecentRange(participant));
  };

  return (
    <Box>
      {/* 이름 + 현재 상태 태그 + 지표 해석 가이드 버튼 */}
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.25, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: colorPalette.textPrimary }}>
          {participant.name}
        </Typography>
        <Typography variant="caption" sx={{ color: colorPalette.textSecondary }}>
          {participant.age ?? "-"}세 · {genderLabel(participant.gender)} · {participant.code}
        </Typography>
        <Box
          component="span"
          sx={{ px: 1.25, py: 0.5, borderRadius: 1.5, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", ...status.tagSx }}
        >
          현재 상태: {status.tag}
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setSopOpen(true)}
          sx={{ ml: "auto", height: 30, px: 1.5, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}
        >
          📖 지표 해석 가이드(SOP)
        </Button>
      </Box>

      {/* 검사 내역 — 현행 유지 */}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: colorPalette.textPrimary }}>
        검사 내역
      </Typography>
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 4 }}>
        <HistoryList label="PHQ-9 우울증 선별검사" type="phq" history={participant.phq_history} />
        <HistoryList label="GAD-7 불안장애 선별검사" type="gad" history={participant.gad_history} />
      </Box>

      {/* 주간 요약 — 4개 한글 비교 카드 */}
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colorPalette.textPrimary }}>
          주간 요약
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
          <TextField
            type="date"
            size="small"
            value={rangeStart}
            onChange={(e) => setRangeStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ "& .MuiInputBase-input": { fontSize: 13, py: 0.7 } }}
          />
          <Typography variant="caption" sx={{ color: colorPalette.textSecondary }}>
            ~
          </Typography>
          <TextField
            type="date"
            size="small"
            value={rangeEnd}
            onChange={(e) => setRangeEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ "& .MuiInputBase-input": { fontSize: 13, py: 0.7 } }}
          />
          <Button size="small" variant="contained" onClick={handleRangeSearch} sx={{ fontSize: 12, px: 1.5 }}>
            조회
          </Button>
        </Box>
      </Box>
      <Typography variant="caption" sx={{ display: "block", mb: 1.5, color: colorPalette.textSecondary }}>
        기간을 비워두고 조회하면 마지막 활동일 기준 최근 7일로 다시 표시됩니다. 수치 대신 단계로 표시하며, 기준은 지표 해석 가이드(SOP)에서 확인할 수 있습니다.
      </Typography>
      <Box sx={{ mb: 4 }}>
        <WeeklySummaryCards week={week} />
      </Box>

      {/* 날짜별 지표 — 3단 구조 유지, 수치는 모두 숨기고 단계로만 표시 (알고리즘 IP 보호) */}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colorPalette.textPrimary }}>
        날짜별 증상 · 감정 · 디스트레스 추이
      </Typography>
      <Typography variant="caption" sx={{ display: "block", mt: 0.5, mb: 2, color: colorPalette.textSecondary }}>
        실선은 개인, 점선은 기관 평균 추이, 검은 점선은 지역 평균 기준선입니다. 종합 디스트레스의 회색 띠는 평소 개인 범위(기준선)이며,
        빨간 테두리의 큰 점은 에피소드(급성 악화) 발생일입니다. 점 위에 마우스를 올리면 그날의 상태 요약이 표시됩니다.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Paper variant="outlined" sx={{ p: 2, backgroundColor: colorPalette.white }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colorPalette.textPrimary, fontSize: 13 }}>
            일별 증상 부담 추이
          </Typography>
          <DailyDualLineChart
            data={buildDailyDualSeries(daily, "symptom_score", "org_symptom_avg")}
            color={colorPalette.primary}
            levels={SIGNAL_CHART_LEVELS}
            metricLabel="증상 부담"
            regionAvg={REGION_AVERAGE.symptomAvg}
          />
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, backgroundColor: colorPalette.white }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colorPalette.textPrimary, fontSize: 13 }}>
            일별 부정 정서 추이
          </Typography>
          <DailyDualLineChart
            data={buildDailyDualSeries(daily, "emo_score", "org_emo_avg")}
            color="#f59e0b"
            levels={SIGNAL_CHART_LEVELS}
            metricLabel="부정 정서"
          />
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, backgroundColor: colorPalette.white }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colorPalette.textPrimary, fontSize: 13 }}>
            일별 종합 디스트레스 추이
          </Typography>
          <DailyDualLineChart
            data={buildDailyDualSeries(daily, "distress_score", "org_distress_avg", "baseline_score")}
            color="#8b5cf6"
            levels={DISTRESS_LEVELS}
            metricLabel="종합 디스트레스"
            stageSuffix
            regionAvg={REGION_AVERAGE.distressAvg}
            showBaseline
          />
          <DistressLevelLegend />
        </Paper>
      </Box>

      <SopGuideDrawer open={sopOpen} onClose={() => setSopOpen(false)} />
    </Box>
  );
};

// ─── 메인 ──────────────────────────────────────────────────────────
/**
 * @param {string} scopeKey - 조회 범위 키 ("ALL" = 전체 기관, 그 외 사업 code)
 * @param {object|null} filter - 종합현황에서 넘어온 초기 필터
 *   { activity: "inactive" } | { distress: "stable"|"caution"|"warning"|"high" } | { participant: 우선 확인 대상자 행 }
 */
const ParticipantsView = ({ scopeKey, filter }) => {
  const dataset = useWsDataset();
  // 종합현황 [상세 보기]로 넘어온 대상자는 목록 맨 앞으로 옮기고 바로 선택
  const participants = useMemo(() => {
    const base = getScopeParticipants(dataset, scopeKey);
    const picked = filter?.participant && base.find((p) => p.user_id === filter.participant.id);
    return picked ? [picked, ...base.filter((p) => p !== picked)] : base;
  }, [dataset, scopeKey, filter]);

  const [keyword, setKeyword] = useState("");
  const [riskFilter, setRiskFilter] = useState(() =>
    filter?.distress ? DISTRESS_TO_RISK_FILTER[filter.distress] : "전체",
  );
  const [activityFilter, setActivityFilter] = useState(() => filter?.activity ?? "전체");
  const [genderFilter, setGenderFilter] = useState("전체");
  const [testFilter, setTestFilter] = useState("전체");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedId, setSelectedId] = useState(() => (filter?.participant ? participants[0]?.user_id ?? null : null));
  const listRef = useRef(null);

  // 페이지 번호가 바뀌면 참가자 명단 스크롤을 맨 위로 이동
  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [currentPage]);

  const filteredList = useMemo(
    () =>
      participants.filter((p) => {
        if (keyword && !p.name.includes(keyword.trim())) return false;
        if (riskFilter !== "전체" && RISK_STATUS[p.status].filter !== riskFilter) return false;
        if (activityFilter !== "전체" && p.activity !== activityFilter) return false;
        if (genderFilter !== "전체" && p.gender !== genderFilter) return false;
        if (testFilter === "PHQ_ANY" && p.phq_history.length === 0) return false;
        if (testFilter === "GAD_ANY" && p.gad_history.length === 0) return false;
        if (testFilter === "NONE" && (p.phq_history.length > 0 || p.gad_history.length > 0)) return false;
        return true;
      }),
    [participants, keyword, riskFilter, activityFilter, genderFilter, testFilter],
  );

  const pageCnt = Math.max(1, Math.ceil(filteredList.length / PAGE_SIZE));
  const pagedList = filteredList.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const selectedParticipant = participants.find((p) => p.user_id === selectedId) ?? null;

  const handleFilterChange = (setter) => (value) => {
    setter(value);
    setCurrentPage(1);
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", gap: 2, overflow: "hidden", p: 3 }}>
      {/* 왼쪽: 참가자 명단 */}
      <Paper
        variant="outlined"
        sx={{
          width: 280,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: colorPalette.white,
        }}
      >
        {/* 검색 & 필터 (2×2) */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${colorPalette.border}`,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flexShrink: 0,
          }}
        >
          <TextField
            size="small"
            placeholder="이름 검색"
            value={keyword}
            onChange={(e) => handleFilterChange(setKeyword)(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: colorPalette.textSecondary }} />
                </InputAdornment>
              ),
            }}
            fullWidth
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
            <Select
              size="small"
              value={riskFilter}
              onChange={(e) => handleFilterChange(setRiskFilter)(e.target.value)}
              sx={filterSelectSx}
            >
              {RISK_FILTER_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              value={activityFilter}
              onChange={(e) => handleFilterChange(setActivityFilter)(e.target.value)}
              sx={filterSelectSx}
            >
              <MenuItem value="전체" sx={{ fontSize: 13 }}>활동상태 전체</MenuItem>
              <MenuItem value="active" sx={{ fontSize: 13 }}>활발</MenuItem>
              <MenuItem value="inactive" sx={{ fontSize: 13 }}>잠잠</MenuItem>
            </Select>
            <Select
              size="small"
              value={genderFilter}
              onChange={(e) => handleFilterChange(setGenderFilter)(e.target.value)}
              sx={filterSelectSx}
            >
              <MenuItem value="전체" sx={{ fontSize: 13 }}>성별 전체</MenuItem>
              <MenuItem value="M" sx={{ fontSize: 13 }}>남</MenuItem>
              <MenuItem value="F" sx={{ fontSize: 13 }}>여</MenuItem>
            </Select>
            <Select
              size="small"
              value={testFilter}
              onChange={(e) => handleFilterChange(setTestFilter)(e.target.value)}
              sx={filterSelectSx}
            >
              <MenuItem value="전체" sx={{ fontSize: 13 }}>검사이력 전체</MenuItem>
              <MenuItem value="PHQ_ANY" sx={{ fontSize: 13 }}>PHQ-9 있음</MenuItem>
              <MenuItem value="GAD_ANY" sx={{ fontSize: 13 }}>GAD-7 있음</MenuItem>
              <MenuItem value="NONE" sx={{ fontSize: 13 }}>이력 없음</MenuItem>
            </Select>
          </Box>
          <Typography variant="caption" sx={{ color: colorPalette.textSecondary }}>
            총 {filteredList.length}명
          </Typography>
        </Box>

        {/* 참가자 명단 */}
        <List ref={listRef} sx={{ flex: 1, overflowY: "auto", p: 0 }}>
          {pagedList.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center", color: colorPalette.textSecondary }}>
              <Typography variant="body2">조회된 참가자가 없습니다.</Typography>
            </Box>
          ) : (
            pagedList.map((p) => (
              <ParticipantListItem
                key={p.user_id}
                participant={p}
                selected={selectedId === p.user_id}
                onClick={() => setSelectedId((prev) => (prev === p.user_id ? null : p.user_id))}
              />
            ))
          )}
        </List>

        <Pagination currentPage={currentPage} pageCnt={pageCnt} onChange={setCurrentPage} />
      </Paper>

      {/* 오른쪽: 선택된 참가자 상세 */}
      <Box sx={{ flex: 1, minWidth: 0, overflowY: "auto", pr: 1 }}>
        {!selectedParticipant ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px dashed ${colorPalette.border}`,
              borderRadius: 1,
              color: colorPalette.textSecondary,
            }}
          >
            <Typography variant="body2">왼쪽 명단에서 참가자를 선택하면 검사 내역이 표시됩니다.</Typography>
          </Box>
        ) : (
          // 참가자가 바뀌면 조회 기간·가이드 상태를 새로 시작하도록 key로 리셋
          <ParticipantDetail key={selectedParticipant.user_id} participant={selectedParticipant} scopeKey={scopeKey} />
        )}
      </Box>
    </Box>
  );
};

export default ParticipantsView;
